import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { getTeamMembers, updateMemberRole, inviteTeamMember, removeTeamMember, logActivity } from '../../../services/teamService';

const TeamManagementTab = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'viewer' });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [roleUpdateLoading, setRoleUpdateLoading] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [removeLoading, setRemoveLoading] = useState(null);

  useEffect(() => {
    loadTeamMembers();
  }, []);

  const loadTeamMembers = async () => {
    setLoading(true);
    try {
      const data = await getTeamMembers();
      setTeamMembers(data);
    } catch (error) {
      console.error('Error loading team members:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (memberId, newRole) => {
    setRoleUpdateLoading(memberId);
    try {
      // Write role directly to user_profiles.role in Supabase
      await updateMemberRole(memberId, newRole);
      // Log as role_change action
      await logActivity('role_change', 'team_member', memberId, { role: newRole });
      // Refresh list to reflect updated role
      await loadTeamMembers();
    } catch (error) {
      console.error('Error updating role:', error);
      alert('Failed to update role. Please try again.');
    } finally {
      setRoleUpdateLoading(null);
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    if (!window.confirm(`Are you sure you want to deactivate ${memberName || 'this member'}? They will lose access to the system.`)) return;
    setRemoveLoading(memberId);
    setOpenActionMenu(null);
    try {
      await removeTeamMember(memberId);
      await logActivity('delete', 'team_member', memberId, { action: 'deactivated' });
      await loadTeamMembers();
    } catch (error) {
      console.error('Error removing member:', error);
      alert('Failed to remove member. Please try again.');
    } finally {
      setRemoveLoading(null);
    }
  };

  const handleInvite = async (e) => {
    e?.preventDefault();
    setInviteLoading(true);
    try {
      // Call inviteTeamMember which creates a user_profiles row with
      // the correct organization_id and status: 'pending'
      await inviteTeamMember({
        email: inviteForm?.email,
        role: inviteForm?.role,
      });
      alert(`Invitation sent to ${inviteForm?.email}. They will appear as pending until they sign up.`);
      setShowInviteModal(false);
      setInviteForm({ email: '', role: 'viewer' });
      // Refresh list so pending member appears
      await loadTeamMembers();
    } catch (error) {
      console.error('Error sending invite:', error);
      alert('Failed to send invitation. Please try again.');
    } finally {
      setInviteLoading(false);
    }
  };

  const filteredMembers = teamMembers?.filter(member =>
    member?.fullName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    member?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-error/10 text-error',
      operator: 'bg-accent/10 text-accent',
      viewer: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
    };
    return colors?.[role] || colors?.viewer;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading team members...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Search and Invite */}
      <div className="bg-card rounded-xl p-4 md:p-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1">
            <div className="relative">
              <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search team members..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>
          <Button onClick={() => setShowInviteModal(true)}>
            <Icon name="UserPlus" size={18} />
            <span>Invite Member</span>
          </Button>
        </div>
      </div>
      {/* Team Members Table */}
      <div className="bg-card rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Member
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Last Active
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredMembers?.map((member) => (
                <tr key={member?.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Icon name="User" size={20} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{member?.fullName}</p>
                        <p className="text-xs text-muted-foreground">{member?.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={member?.role}
                      onChange={(e) => handleRoleChange(member?.id, e?.target?.value)}
                      disabled={roleUpdateLoading === member?.id}
                      className={`px-3 py-1 rounded-full text-xs font-medium border-0 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 ${getRoleBadgeColor(member?.role)}`}
                    >
                      <option value="admin">Admin</option>
                      <option value="operator">Operator</option>
                      <option value="viewer">Viewer</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      member?.status === 'pending' ?'bg-warning/10 text-warning'
                        : member?.status === 'inactive' ?'bg-muted text-muted-foreground' :'bg-success/10 text-success'
                    }`}>
                      {member?.status ? member?.status?.charAt(0)?.toUpperCase() + member?.status?.slice(1) : 'Active'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    {formatDate(member?.lastActiveAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="relative">
                      <button
                        onClick={() => setOpenActionMenu(openActionMenu === member?.id ? null : member?.id)}
                        className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded"
                        disabled={removeLoading === member?.id}
                      >
                        {removeLoading === member?.id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                        ) : (
                          <Icon name="MoreVertical" size={18} />
                        )}
                      </button>
                      {openActionMenu === member?.id && (
                        <div className="absolute right-0 mt-1 w-44 bg-card border border-border rounded-lg shadow-lg z-10">
                          <button
                            onClick={() => { setOpenActionMenu(null); }}
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-muted/50 transition-colors"
                          >
                            <Icon name="User" size={16} />
                            <span>View Profile</span>
                          </button>
                          <button
                            onClick={() => handleRemoveMember(member?.id, member?.fullName)}
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-error hover:bg-error/10 transition-colors"
                          >
                            <Icon name="UserX" size={16} />
                            <span>Remove Member</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl p-6 max-w-md w-full shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Invite Team Member</h3>
              <button
                onClick={() => setShowInviteModal(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            <form onSubmit={handleInvite} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Email Address</label>
                <Input
                  type="email"
                  placeholder="colleague@example.com"
                  value={inviteForm?.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e?.target?.value })}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Role</label>
                <Select
                  value={inviteForm?.role}
                  onChange={(e) => setInviteForm({ ...inviteForm, role: e?.target?.value })}
                  options={[
                    { value: 'admin', label: 'Admin - Full access' },
                    { value: 'operator', label: 'Operator - Manage projects' },
                    { value: 'viewer', label: 'Viewer - View only' }
                  ]}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                The invited member will be added with a <strong>pending</strong> status and linked to your organization. They can log in once their account is set up.
              </p>
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowInviteModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={inviteLoading} className="flex-1">
                  {inviteLoading ? 'Sending...' : 'Send Invite'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManagementTab;